import { useState, useEffect } from 'react';
import { fetchFieldOptions } from '../services/baserow/fields';
import { BASEROW_CONFIG } from '../services/baserow/config';

export interface Subtopic {
  id: number;
  value: string;
}

const CACHE_KEY = 'baserow_subtopics';
const CACHE_DURATION = 1000 * 60 * 5; // 5 minutes

interface CachedData {
  subtopics: Subtopic[];
  timestamp: number;
}

export function useSubtopics() {
  const [subtopics, setSubtopics] = useState<Subtopic[]>(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { subtopics, timestamp }: CachedData = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_DURATION) {
          return subtopics;
        }
      }
    } catch (e) {
      console.warn('Failed to load cached subtopics:', e);
    }
    return [];
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let mounted = true;
    const maxRetries = 3;
    const retryDelay = 1000; // 1 second

    const loadSubtopics = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const options = await fetchFieldOptions(BASEROW_CONFIG.FIELDS.SUBTOPIC);
        const newSubtopics = options.map(({ id, value }) => ({ id, value }));
        
        if (mounted) {
          setSubtopics(newSubtopics);
          // Cache the results
          localStorage.setItem(CACHE_KEY, JSON.stringify({
            subtopics: newSubtopics,
            timestamp: Date.now()
          }));
        }
      } catch (err) {
        if (mounted) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to load subtopics';
          console.error('Subtopics loading error:', errorMessage);
          
          if (retryCount < maxRetries) {
            setTimeout(() => {
              setRetryCount(prev => prev + 1);
            }, retryDelay * (retryCount + 1)); // Exponential backoff
          } else {
            setError(errorMessage);
          }
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadSubtopics();

    return () => {
      mounted = false;
    };
  }, [retryCount]);

  const reload = () => {
    localStorage.removeItem(CACHE_KEY);
    setRetryCount(0);
  };

  return { 
    subtopics, 
    loading: loading && subtopics.length === 0, 
    error, 
    reload 
  };
}